/**
 * Isolation Domain Seed Data for E2E Testing
 *
 * Contains seed data for testing Multi-Tenancy Isolation scenarios (Issue #278):
 * - Two isolated tenants (Alpha, Beta) for cross-tenant testing
 * - Isolated realms within each tenant
 * - Isolated businesses within each realm
 * - Users and consumers in each business
 * - Multi-realm user for realm switching tests
 *
 * @module seed/domains/isolation
 * @see Issue #278 for Multi-Tenancy Isolation E2E scenarios
 */

import { seedId } from '../utils';
// @see Issue #296 - PR review feedback: removed unused imports
// CONSUMER_IDS, BUSINESS_IDS, REALM_IDS, TENANT_IDS, USER_IDS were imported
// but not used - isolation domain uses its own ISOLATION_*_IDS via seedId()

// =============================================================================
// ISOLATION TENANT IDs (Issue #278 - Scenarios 81-96)
// =============================================================================

/**
 * Isolation test tenant IDs
 * These are dedicated tenants for multi-tenancy isolation testing
 */
export const ISOLATION_TENANT_IDS = {
  // Tenant Alpha (primary test tenant)
  TENANT_ALPHA: seedId('isolation-tenant-alpha', 1),
  // Tenant Beta (secondary tenant for cross-tenant isolation)
  TENANT_BETA: seedId('isolation-tenant-beta', 2),
} as const;

export type IsolationTenantId = (typeof ISOLATION_TENANT_IDS)[keyof typeof ISOLATION_TENANT_IDS];

// =============================================================================
// ISOLATION REALM IDs (Issue #278)
// =============================================================================

/**
 * Isolation test realm IDs
 * Each tenant has one realm for testing cross-realm isolation
 */
export const ISOLATION_REALM_IDS = {
  // Realm Alpha (within Tenant Alpha)
  REALM_ALPHA: seedId('isolation-realm-alpha', 1),
  // Realm Beta (within Tenant Beta)
  REALM_BETA: seedId('isolation-realm-beta', 2),
  // Realm for multi-realm user testing (in Tenant Alpha)
  REALM_MULTI_TEST: seedId('isolation-realm-multi', 3),
} as const;

export type IsolationRealmId = (typeof ISOLATION_REALM_IDS)[keyof typeof ISOLATION_REALM_IDS];

// =============================================================================
// ISOLATION BUSINESS IDs (Issue #278)
// =============================================================================

/**
 * Isolation test business IDs
 * Each realm has one business for testing cross-business isolation
 */
export const ISOLATION_BUSINESS_IDS = {
  // Business Alpha (within Realm Alpha)
  BUSINESS_ALPHA: seedId('isolation-business-alpha', 1),
  // Business Beta (within Realm Beta)
  BUSINESS_BETA: seedId('isolation-business-beta', 2),
  // Business for multi-realm testing (in Realm Multi Test)
  BUSINESS_MULTI_TEST: seedId('isolation-business-multi', 3),
} as const;

export type IsolationBusinessId = (typeof ISOLATION_BUSINESS_IDS)[keyof typeof ISOLATION_BUSINESS_IDS];

// =============================================================================
// ISOLATION USER IDs (Issue #278)
// =============================================================================

/**
 * Isolation test user IDs
 * Users dedicated to testing multi-tenancy isolation
 */
export const ISOLATION_USER_IDS = {
  // User in Business Alpha (for cross-tenant/business tests)
  USER_ALPHA: seedId('isolation-user-alpha', 1),
  // User in Business Beta (for cross-tenant/business tests)
  USER_BETA: seedId('isolation-user-beta', 2),
  // Multi-realm user (has access to multiple realms)
  USER_MULTI_REALM: seedId('isolation-user-multirealm', 3),
  // Admin user for audit immutability tests
  USER_AUDIT_ADMIN: seedId('isolation-user-audit-admin', 4),
  // 🆕 PR #295: Role-based users for CASL Permission Guard scenarios
  // Business admin with ALL permissions (for guard_allows_authorized)
  USER_CASL_ADMIN: seedId('isolation-user-casl-admin', 5),
  // Business viewer with read-only permissions (for guard_blocks_unauthorized)
  USER_CASL_VIEWER: seedId('isolation-user-casl-viewer', 6),
  // Business user with partial permissions (for guard_multi_permission test)
  USER_CASL_STANDARD: seedId('isolation-user-casl-standard', 7),
} as const;

export type IsolationUserId = (typeof ISOLATION_USER_IDS)[keyof typeof ISOLATION_USER_IDS];

// =============================================================================
// ISOLATION CONSUMER IDs (Issue #278)
// =============================================================================

/**
 * Isolation test consumer IDs
 * Consumers dedicated to testing business/realm isolation
 */
export const ISOLATION_CONSUMER_IDS = {
  // Consumer in Business Alpha
  CONSUMER_ALPHA: seedId('isolation-consumer-alpha', 1),
  // Consumer in Business Beta
  CONSUMER_BETA: seedId('isolation-consumer-beta', 2),
} as const;

export type IsolationConsumerId = (typeof ISOLATION_CONSUMER_IDS)[keyof typeof ISOLATION_CONSUMER_IDS];

// =============================================================================
// ISOLATION CASE IDs (Issue #278)
// =============================================================================

/**
 * Isolation test case IDs
 * Cases dedicated to testing realm schema isolation
 */
export const ISOLATION_CASE_IDS = {
  // Case in Realm Alpha schema
  CASE_ALPHA: seedId('isolation-case-alpha', 1),
  // Case in Realm Beta schema
  CASE_BETA: seedId('isolation-case-beta', 2),
} as const;

export type IsolationCaseId = (typeof ISOLATION_CASE_IDS)[keyof typeof ISOLATION_CASE_IDS];

// =============================================================================
// ISOLATION ENTITY IDs (Issue #278)
// =============================================================================

/**
 * Isolation test entity IDs
 * Entity records dedicated to testing business isolation
 */
export const ISOLATION_ENTITY_IDS = {
  // Entity in Business Alpha
  ENTITY_ALPHA: seedId('isolation-entity-alpha', 1),
  // Entity in Business Beta
  ENTITY_BETA: seedId('isolation-entity-beta', 2),
} as const;

export type IsolationEntityId = (typeof ISOLATION_ENTITY_IDS)[keyof typeof ISOLATION_ENTITY_IDS];

// =============================================================================
// ISOLATION API KEY IDs (Issue #278)
// =============================================================================

/**
 * Isolation test API key IDs
 * API keys dedicated to testing scope isolation
 */
export const ISOLATION_API_KEY_IDS = {
  // API key for Business Alpha
  API_KEY_ALPHA: seedId('isolation-apikey-alpha', 1),
  // API key for Business Beta
  API_KEY_BETA: seedId('isolation-apikey-beta', 2),
} as const;

export type IsolationApiKeyId = (typeof ISOLATION_API_KEY_IDS)[keyof typeof ISOLATION_API_KEY_IDS];

// =============================================================================
// CONSOLIDATED ISOLATION_SEED_IDS (Issue #278)
// =============================================================================

/**
 * All isolation-related seed IDs consolidated for E2E scenarios
 *
 * @example
 * ```typescript
 * import { ISOLATION_SEED_IDS } from '@ops-matrix/shared-types';
 *
 * const tenantId = ISOLATION_SEED_IDS.tenants.TENANT_ALPHA;
 * const businessId = ISOLATION_SEED_IDS.businesses.BUSINESS_ALPHA;
 * ```
 */
export const ISOLATION_SEED_IDS = {
  tenants: ISOLATION_TENANT_IDS,
  realms: ISOLATION_REALM_IDS,
  businesses: ISOLATION_BUSINESS_IDS,
  users: ISOLATION_USER_IDS,
  consumers: ISOLATION_CONSUMER_IDS,
  cases: ISOLATION_CASE_IDS,
  entities: ISOLATION_ENTITY_IDS,
  apiKeys: ISOLATION_API_KEY_IDS,
} as const;

export type IsolationSeedIds = typeof ISOLATION_SEED_IDS;

// =============================================================================
// TENANT DATA TYPE
// =============================================================================

export interface IsolationTenantSeedData {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended' | 'archived';
  tier: 'free' | 'starter' | 'professional' | 'enterprise';
  createdAt: string;
}

// =============================================================================
// REALM DATA TYPE
// =============================================================================

export interface IsolationRealmSeedData {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  schemaName: string;
  template: 'blank' | 'los' | 'custom';
  status: 'active' | 'suspended' | 'archived';
  createdAt: string;
}

// =============================================================================
// BUSINESS DATA TYPE
// =============================================================================

export interface IsolationBusinessSeedData {
  id: string;
  realmId: string;
  name: string;
  slug: string;
  isActive: boolean;
  createdAt: string;
}

// =============================================================================
// USER DATA TYPE
// =============================================================================

export interface IsolationUserSeedData {
  id: string;
  email: string;
  name: string;
  businessId: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  isActive: boolean;
  createdAt: string;
}

// =============================================================================
// CONSUMER DATA TYPE
// =============================================================================

export interface IsolationConsumerSeedData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  businessId: string;
  realmId: string;
  consumerType: 'BORROWER' | 'REFERRAL_PARTNER' | 'CO_BORROWER';
  status: 'active' | 'suspended' | 'invited';
  createdAt: string;
}

// =============================================================================
// TENANT DATA
// =============================================================================

export const ISOLATION_TENANTS: Record<keyof typeof ISOLATION_TENANT_IDS, IsolationTenantSeedData> = {
  TENANT_ALPHA: {
    id: ISOLATION_TENANT_IDS.TENANT_ALPHA,
    name: 'Isolation Tenant Alpha',
    slug: 'isolation-tenant-alpha',
    status: 'active',
    tier: 'professional',
    createdAt: '2026-01-01T00:00:00Z',
  },
  TENANT_BETA: {
    id: ISOLATION_TENANT_IDS.TENANT_BETA,
    name: 'Isolation Tenant Beta',
    slug: 'isolation-tenant-beta',
    status: 'active',
    tier: 'professional',
    createdAt: '2026-01-01T00:00:00Z',
  },
};

// =============================================================================
// REALM DATA
// =============================================================================

export const ISOLATION_REALMS: Record<keyof typeof ISOLATION_REALM_IDS, IsolationRealmSeedData> = {
  REALM_ALPHA: {
    id: ISOLATION_REALM_IDS.REALM_ALPHA,
    tenantId: ISOLATION_TENANT_IDS.TENANT_ALPHA,
    name: 'Realm Alpha',
    slug: 'realm-alpha',
    schemaName: 'realm_isolation_alpha',
    template: 'los',
    status: 'active',
    createdAt: '2026-01-01T00:00:00Z',
  },
  REALM_BETA: {
    id: ISOLATION_REALM_IDS.REALM_BETA,
    tenantId: ISOLATION_TENANT_IDS.TENANT_BETA,
    name: 'Realm Beta',
    slug: 'realm-beta',
    schemaName: 'realm_isolation_beta',
    template: 'los',
    status: 'active',
    createdAt: '2026-01-01T00:00:00Z',
  },
  REALM_MULTI_TEST: {
    id: ISOLATION_REALM_IDS.REALM_MULTI_TEST,
    tenantId: ISOLATION_TENANT_IDS.TENANT_ALPHA,
    name: 'Multi-Realm Test',
    slug: 'realm-multi-test',
    schemaName: 'realm_isolation_multi',
    template: 'blank',
    status: 'active',
    createdAt: '2026-01-01T00:00:00Z',
  },
};

// =============================================================================
// BUSINESS DATA
// =============================================================================

export const ISOLATION_BUSINESSES: Record<keyof typeof ISOLATION_BUSINESS_IDS, IsolationBusinessSeedData> = {
  BUSINESS_ALPHA: {
    id: ISOLATION_BUSINESS_IDS.BUSINESS_ALPHA,
    realmId: ISOLATION_REALM_IDS.REALM_ALPHA,
    name: 'Business Alpha',
    slug: 'business-alpha',
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  BUSINESS_BETA: {
    id: ISOLATION_BUSINESS_IDS.BUSINESS_BETA,
    realmId: ISOLATION_REALM_IDS.REALM_BETA,
    name: 'Business Beta',
    slug: 'business-beta',
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  BUSINESS_MULTI_TEST: {
    id: ISOLATION_BUSINESS_IDS.BUSINESS_MULTI_TEST,
    realmId: ISOLATION_REALM_IDS.REALM_MULTI_TEST,
    name: 'Business Multi Test',
    slug: 'business-multi-test',
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
};

// =============================================================================
// USER DATA
// =============================================================================

export const ISOLATION_USERS: Record<keyof typeof ISOLATION_USER_IDS, IsolationUserSeedData> = {
  USER_ALPHA: {
    id: ISOLATION_USER_IDS.USER_ALPHA,
    email: 'user-alpha@isolation.opsmatrix.test',
    name: 'User Alpha',
    businessId: ISOLATION_BUSINESS_IDS.BUSINESS_ALPHA,
    role: 'admin',
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  USER_BETA: {
    id: ISOLATION_USER_IDS.USER_BETA,
    email: 'user-beta@isolation.opsmatrix.test',
    name: 'User Beta',
    businessId: ISOLATION_BUSINESS_IDS.BUSINESS_BETA,
    role: 'admin',
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  USER_MULTI_REALM: {
    id: ISOLATION_USER_IDS.USER_MULTI_REALM,
    email: 'user-multirealm@isolation.opsmatrix.test',
    name: 'User Multi-Realm',
    businessId: ISOLATION_BUSINESS_IDS.BUSINESS_ALPHA, // Primary business
    role: 'member',
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  USER_AUDIT_ADMIN: {
    id: ISOLATION_USER_IDS.USER_AUDIT_ADMIN,
    email: 'user-audit-admin@isolation.opsmatrix.test',
    name: 'Audit Admin',
    businessId: ISOLATION_BUSINESS_IDS.BUSINESS_ALPHA,
    role: 'admin',
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  // 🆕 PR #295: Role-based users for CASL Permission Guard scenarios
  USER_CASL_ADMIN: {
    id: ISOLATION_USER_IDS.USER_CASL_ADMIN,
    email: 'user-casl-admin@isolation.opsmatrix.test',
    name: 'CASL Admin',
    businessId: ISOLATION_BUSINESS_IDS.BUSINESS_ALPHA,
    role: 'admin', // Has ALL permissions
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  USER_CASL_VIEWER: {
    id: ISOLATION_USER_IDS.USER_CASL_VIEWER,
    email: 'user-casl-viewer@isolation.opsmatrix.test',
    name: 'CASL Viewer',
    businessId: ISOLATION_BUSINESS_IDS.BUSINESS_ALPHA,
    role: 'viewer', // Read-only, lacks users:create permission
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  USER_CASL_STANDARD: {
    id: ISOLATION_USER_IDS.USER_CASL_STANDARD,
    email: 'user-casl-standard@isolation.opsmatrix.test',
    name: 'CASL Standard User',
    businessId: ISOLATION_BUSINESS_IDS.BUSINESS_ALPHA,
    role: 'member', // Has some permissions but not analytics:export
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
};

// =============================================================================
// CONSUMER DATA
// =============================================================================

export const ISOLATION_CONSUMERS: Record<keyof typeof ISOLATION_CONSUMER_IDS, IsolationConsumerSeedData> = {
  CONSUMER_ALPHA: {
    id: ISOLATION_CONSUMER_IDS.CONSUMER_ALPHA,
    email: 'consumer-alpha@isolation.test',
    firstName: 'Consumer',
    lastName: 'Alpha',
    businessId: ISOLATION_BUSINESS_IDS.BUSINESS_ALPHA,
    realmId: ISOLATION_REALM_IDS.REALM_ALPHA,
    consumerType: 'BORROWER',
    status: 'active',
    createdAt: '2026-01-01T00:00:00Z',
  },
  CONSUMER_BETA: {
    id: ISOLATION_CONSUMER_IDS.CONSUMER_BETA,
    email: 'consumer-beta@isolation.test',
    firstName: 'Consumer',
    lastName: 'Beta',
    businessId: ISOLATION_BUSINESS_IDS.BUSINESS_BETA,
    realmId: ISOLATION_REALM_IDS.REALM_BETA,
    consumerType: 'BORROWER',
    status: 'active',
    createdAt: '2026-01-01T00:00:00Z',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllIsolationTenants(): IsolationTenantSeedData[] {
  return Object.values(ISOLATION_TENANTS);
}

export function getIsolationTenantById(id: string): IsolationTenantSeedData | undefined {
  return Object.values(ISOLATION_TENANTS).find((t) => t.id === id);
}

export function getAllIsolationRealms(): IsolationRealmSeedData[] {
  return Object.values(ISOLATION_REALMS);
}

export function getIsolationRealmsByTenant(tenantId: string): IsolationRealmSeedData[] {
  return Object.values(ISOLATION_REALMS).filter((r) => r.tenantId === tenantId);
}

export function getAllIsolationBusinesses(): IsolationBusinessSeedData[] {
  return Object.values(ISOLATION_BUSINESSES);
}

export function getIsolationBusinessesByRealm(realmId: string): IsolationBusinessSeedData[] {
  return Object.values(ISOLATION_BUSINESSES).filter((b) => b.realmId === realmId);
}

export function getAllIsolationUsers(): IsolationUserSeedData[] {
  return Object.values(ISOLATION_USERS);
}

export function getIsolationUsersByBusiness(businessId: string): IsolationUserSeedData[] {
  return Object.values(ISOLATION_USERS).filter((u) => u.businessId === businessId);
}

export function getAllIsolationConsumers(): IsolationConsumerSeedData[] {
  return Object.values(ISOLATION_CONSUMERS);
}

export function getIsolationConsumersByBusiness(businessId: string): IsolationConsumerSeedData[] {
  return Object.values(ISOLATION_CONSUMERS).filter((c) => c.businessId === businessId);
}

/**
 * Get all isolation seed data for a complete E2E isolation test setup
 */
export function getIsolationTestSetup() {
  return {
    tenants: ISOLATION_TENANTS,
    realms: ISOLATION_REALMS,
    businesses: ISOLATION_BUSINESSES,
    users: ISOLATION_USERS,
    consumers: ISOLATION_CONSUMERS,
    ids: ISOLATION_SEED_IDS,
  };
}

/**
 * Get Alpha tenant hierarchy (for positive tests)
 */
export function getAlphaTenantHierarchy() {
  return {
    tenant: ISOLATION_TENANTS.TENANT_ALPHA,
    realm: ISOLATION_REALMS.REALM_ALPHA,
    business: ISOLATION_BUSINESSES.BUSINESS_ALPHA,
    user: ISOLATION_USERS.USER_ALPHA,
    consumer: ISOLATION_CONSUMERS.CONSUMER_ALPHA,
  };
}

/**
 * Get Beta tenant hierarchy (for cross-tenant isolation tests)
 */
export function getBetaTenantHierarchy() {
  return {
    tenant: ISOLATION_TENANTS.TENANT_BETA,
    realm: ISOLATION_REALMS.REALM_BETA,
    business: ISOLATION_BUSINESSES.BUSINESS_BETA,
    user: ISOLATION_USERS.USER_BETA,
    consumer: ISOLATION_CONSUMERS.CONSUMER_BETA,
  };
}
